import { DataSource } from '../Widget';
import { FilterState } from '../../Filter/types';

// Utility function to set nested object values using dot notation
// Block prototype pollution by disallowing dangerous property names
const isUnsafeKey = (key: string) =>
  key === '__proto__' || key === 'constructor' || key === 'prototype';

export const setNestedValue = (
  obj: Record<string, unknown>,
  path: string,
  value: unknown,
): void => {
  const keys = path.split('.');
  let current: Record<string, unknown> = obj;

  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    if (isUnsafeKey(key)) {
      // Skip or throw; here we skip assignment for safety
      return;
    }
    if (!(key in current) || typeof current[key] !== 'object') {
      current[key] = {};
    }
    current = current[key] as Record<string, unknown>;
  }

  const lastKey = keys[keys.length - 1];
  if (isUnsafeKey(lastKey)) {
    return;
  }
  current[lastKey] = value;
};

// Utility function to get nested object values using dot notation
export const getNestedValue = (
  obj: Record<string, unknown>,
  path: string,
): unknown => {
  return path.split('.').reduce((current: unknown, key: string) => {
    if (current && typeof current === 'object' && key in current) {
      return (current as Record<string, unknown>)[key];
    }
    return undefined;
  }, obj);
};

// Check if a filter value has a meaningful value (not null, undefined, or empty)
export const hasValidFilterValue = (value: unknown): boolean => {
  if (value === null || value === undefined) {
    return false;
  }

  // Check for empty arrays
  if (Array.isArray(value)) {
    return value.length > 0;
  }

  // Check for empty date range objects
  if (
    typeof value === 'object' &&
    value !== null &&
    'start' in value &&
    'end' in value
  ) {
    return value.start !== null || value.end !== null;
  }

  // Check for empty strings
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }

  // All other values are considered valid
  return true;
};

// Transform filter values based on type
export const transformFilterValue = (
  value: unknown,
  transform?: (value: unknown) => unknown,
): unknown => {
  // Apply custom transformation if provided
  if (transform) {
    const transformed = transform(value);
    // Ensure the transformed value is still valid
    return hasValidFilterValue(transformed) ? transformed : undefined;
  }

  // Default transformations based on value type
  if (value === null || value === undefined) {
    return undefined;
  }

  // Handle date range objects
  if (
    typeof value === 'object' &&
    value !== null &&
    'start' in value &&
    'end' in value
  ) {
    const dateRange = value as { start: Date | null; end: Date | null };
    const { start, end } = dateRange;
    if (start && end) {
      return {
        start: start.toISOString(),
        end: end.toISOString(),
      };
    } else if (start) {
      return {
        start: start.toISOString(),
      };
    } else if (end) {
      return {
        end: end.toISOString(),
      };
    }
    return undefined;
  }

  // Handle arrays (multi-select) - only return if not empty
  if (Array.isArray(value)) {
    return value.length > 0 ? value : undefined;
  }

  // Handle strings - only return if not empty after trimming
  if (typeof value === 'string') {
    return value.trim().length > 0 ? value : undefined;
  }

  // Handle primitive values (numbers, booleans, etc.)
  return value;
};

// Clean up parameters object by removing null/undefined values
export const cleanParameters = (obj: unknown): unknown => {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj
      .map(cleanParameters)
      .filter((item) => item !== null && item !== undefined);
  }

  if (typeof obj === 'object') {
    const cleaned: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj)) {
      const cleanedValue = cleanParameters(value);
      if (cleanedValue !== null && cleanedValue !== undefined) {
        cleaned[key] = cleanedValue;
      }
    }
    return cleaned;
  }

  return obj;
};

// Resolve JSONPath expression against filter state
export const resolveJsonPath = (
  jsonPath: string,
  filterState: FilterState,
): unknown => {
  // Simple JSONPath resolver for filter expressions
  // Supports: $.filter.filterId, $.filter.filterId.property, etc.

  if (!jsonPath.startsWith('$.filter.')) {
    console.warn(
      `Invalid JSONPath for filters: ${jsonPath}. Must start with $.filter.`,
    );
    return undefined;
  }

  const pathParts = jsonPath.replace('$.filter.', '').split('.');
  let current: unknown = filterState;

  for (const part of pathParts) {
    if (current === null || current === undefined) {
      return undefined;
    }

    if (typeof current === 'object' && current !== null) {
      current = (current as Record<string, unknown>)[part];
    } else {
      return undefined;
    }
  }

  return current;
};

// Resolve a structure (array, object, or value) against filter state
export const resolveStructure = (
  structure: unknown,
  filterState: FilterState,
): unknown => {
  if (typeof structure === 'string') {
    // It's a JSONPath expression
    return resolveJsonPath(structure, filterState);
  }

  if (Array.isArray(structure)) {
    // It's an array - resolve each element
    return structure.map((item) => resolveStructure(item, filterState));
  }

  if (typeof structure === 'object' && structure !== null) {
    // It's an object - resolve each property
    const resolved: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(structure)) {
      resolved[key] = resolveStructure(value, filterState);
    }
    return resolved;
  }

  // It's a primitive value - return as-is
  return structure;
};

// Apply JSONPath-based filter parameters to data source
export const applyJsonPathFilters = (
  dataSource: DataSource,
  filterState: FilterState,
): DataSource => {
  if (!dataSource.filterParameterPaths) {
    return dataSource;
  }

  // Create a deep copy of the data source
  const filteredDataSource: DataSource = {
    ...dataSource,
    parameters: JSON.parse(JSON.stringify(dataSource.parameters)),
  };

  // Apply each filter parameter path
  Object.entries(dataSource.filterParameterPaths).forEach(
    ([paramKey, structure]) => {
      const resolvedValue = resolveStructure(structure, filterState);

      // Only proceed if the resolved value is valid
      if (!hasValidFilterValue(resolvedValue)) {
        return; // Skip this parameter - don't inject anything
      }

      // Set the parameter directly
      filteredDataSource.parameters[paramKey] = resolvedValue;
    },
  );

  // Clean up the parameters object to remove any null/undefined values
  const cleanedParams = cleanParameters(
    filteredDataSource.parameters,
  ) as Record<string, unknown>;

  // Ensure intention property is preserved
  if (!cleanedParams.intention && filteredDataSource.parameters.intention) {
    cleanedParams.intention = filteredDataSource.parameters.intention;
  }

  filteredDataSource.parameters = cleanedParams as {
    intention: string;
    [key: string]: unknown;
  };

  return filteredDataSource;
};

// Apply filter subscriptions to data source parameters
export const applyFiltersToDataSource = (
  dataSource: DataSource,
  filterState: FilterState,
): DataSource => {
  // Use JSONPath approach if available
  if (dataSource.filterParameterPaths) {
    return applyJsonPathFilters(dataSource, filterState);
  }

  // No filters configured
  return dataSource;
};
